import { consume } from '@lit/context';
import { html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { PageMeta } from '../../cross-framework-lib/insights-service/insights.models';
import '../../design-system/progress/progress';
import '../../shared/components/content-layout/content-layout';
import '../../shared/components/router/router';
import '../../shared/components/time-tracking/time-tracking';
import '../message-instructor/message-instructor-view';
import './course-player-footer';
import './course-player-header';
import './course-player-sidenav';
import { CoursePlayerState, stateContext } from './course-player.context';
import { routes } from './routes';

@customElement('lua-course-player-view')
export class LuaCoursePlayerView extends LitElement {
  @property({ type: Number })
  currentModuleIndex = 0;

  @property({ type: Number })
  activeSlideStyleTopValue = 0;

  @consume({ context: stateContext, subscribe: true })
  coursePlayerState: CoursePlayerState;

  private getLayout(): 'default' | 'sidebar-only' {
    return 'default';
  }

  private customizeLayout() {
    if (this.coursePlayerState.canShowCompletionBar()) {
      this.style.setProperty('--content-layout-header-height', '90px');
    }
    if (this.coursePlayerState.isTakingExam) {
      this.style.setProperty('--content-layout-header-height', '0px');
    }
  }

  private getPageMeta(): PageMeta {
    const pageGroup = 'enrollments';

    if (this.coursePlayerState.isPreview) {
      return {
        pagegroup: 'courses',
        pagename: 'preview',
      };
    }

    if (this.coursePlayerState.onDescriptionPage) {
      return {
        pagegroup: pageGroup,
        pagename: 'course_description',
      };
    }

    return {
      pagegroup: pageGroup,
      pagename: 'course_player',
    };
  }

  override render() {
    const route = this.coursePlayerState.currentRoute;
    const layoutVariant = this.getLayout();
    const shouldShowHeader =
      this.coursePlayerState.isPreview ||
      this.coursePlayerState.showExitModuleButton ||
      !this.coursePlayerState.isTakingExam;
    const pageMeta = this.getPageMeta();

    this.customizeLayout();
    return html`
      <lua-content-layout
        .variant="${layoutVariant}"
        ?open=${this.coursePlayerState.isSidenavOpen}
        ?hideFooter=${this.coursePlayerState.isTakingExam}
        ?isSidebarDisabled=${this.coursePlayerState.isSidenavDisabled || this.coursePlayerState.isTakingExam}>
        ${shouldShowHeader ? html`<lua-course-player-header slot="header"></lua-course-player-header>` : nothing}
        <lua-course-player-sidenav slot="sidenav"></lua-course-player-sidenav>
        <lua-router .currentRoute="${route}" .routes="${routes}" slot="content">
          <slot></slot>
        </lua-router>
        ${!this.coursePlayerState.isTakingExam
          ? html`<lua-course-player-footer slot="footer"></lua-course-player-footer>`
          : nothing}
      </lua-content-layout>
      <lua-time-tracking
        courseId=${ifDefined(this.coursePlayerState.enrollment?.course_id)}
        moduleId=${ifDefined(this.coursePlayerState.currentModule?.component_id)}
        pagegroup=${pageMeta.pagegroup}
        pagename=${pageMeta.pagename}></lua-time-tracking>
      <lua-message-instructor
        ?open="${this.coursePlayerState.openInstructorMessage}"
        enrollmentId="${this.coursePlayerState.enrollment?.id}"
        @close-instructor="${() => this.coursePlayerState.toggleInstructorMessage(false)}"></lua-message-instructor>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lua-course-player-view': LuaCoursePlayerView;
  }
}
